"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, PhoneCall, ArrowRight, ShieldCheck, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const keyNeighborhoods = [{
  name: "Downtown Jonesboro",
  responseTime: "15-30 Min Response"
}, {
  name: "Tara Boulevard Corridor",
  responseTime: "15-30 Min Response"
}, {
  name: "Lake Spivey Area",
  responseTime: "20-35 Min Response"
}, {
  name: "Flint River Road District",
  responseTime: "15-30 Min Response"
}, {
  name: "Irondale Community",
  responseTime: "20-35 Min Response"
}, {
  name: "All Surrounding Clayton County",
  responseTime: "24/7 Dispatch"
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const motionProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: "-50px"
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="jonesboroservicearea" data-nav="light" className="relative bg-background py-16 sm:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Main Info Column */}
          <motion.div {...motionProps} className="lg:col-span-7 flex flex-col items-start space-y-6">
            <Badge variant="outline" className="border-accent/40 bg-accent/10 text-foreground font-semibold px-3 py-1 gap-1.5">
              <MapPin className="size-3.5 text-accent" />
              Clayton County Local Coverage
            </Badge>

            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
                Plumbing Services in <span className="text-primary">Jonesboro, GA</span>
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
                When burst pipes, severe sewer backups, or failing water heaters threaten your home, our licensed technicians are ready. We provide rapid 24/7 emergency response throughout all Jonesboro neighborhoods and broader Clayton County communities with transparent upfront estimates.
              </p>
            </div>

            {/* Coverage highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 w-full pt-2">
              <div className="flex items-center gap-3 p-3.5 rounded-lg bg-card border border-border">
                <Clock className="size-5 text-accent shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-foreground block">Immediate 24/7 Dispatch</span>
                  <span className="text-muted-foreground text-xs">Day, night, weekends & holidays</span>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3.5 rounded-lg bg-card border border-border">
                <ShieldCheck className="size-5 text-accent shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-foreground block">Licensed & Insured</span>
                  <span className="text-muted-foreground text-xs">Master plumbers on call</span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-sm">
                <Link to="/service-areas/" className="inline-flex items-center gap-2">
                  <span>View Service Areas</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-medium">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4 text-accent" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Neighborhood Card Column */}
          <motion.div {...motionProps} transition={{
          duration: 0.5,
          delay: shouldReduceMotion ? 0 : 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <Card className="border-border bg-card shadow-md relative overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-1.5 bg-accent" />
              
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div className="space-y-1.5">
                  <h3 className="text-xl font-bold font-default text-foreground">
                    Neighborhood Coverage
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Direct local technician routing across the entire Jonesboro metro area.
                  </p>
                </div>

                <div className="divide-y divide-border border-y border-border">
                  {keyNeighborhoods.map((item, i) => <div key={item.name} data-index={i} className="py-3 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2.5 font-medium text-foreground">
                        <CheckCircle2 className="size-4 text-accent shrink-0" />
                        <span>{item.name}</span>
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">
                        {item.responseTime}
                      </span>
                    </div>)}
                </div>

                <div className="bg-primary/5 rounded-lg p-4 border border-primary/10 flex items-start gap-3">
                  <div className="size-2 rounded-full bg-accent mt-2 shrink-0 animate-pulse" />
                  <p className="text-xs text-foreground/80 leading-relaxed font-sans">
                    <strong>Need immediate emergency service?</strong> Our fully equipped mobile response vans carry standard replacement parts to resolve most urgent repairs on the spot.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}